import { Button, Icon, PillBadge } from '@platform/components';
import cx from 'classix';
import { useId, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';

export type TimelineStage = 'completed' | 'in-progress' | 'not-started';

export type TimelineLayoutSize = 'desktop' | 'mobile';

export type TimelinePanelCta = {
  label: string;
  onClick: () => void;
};

export type TimelinePanelContent = {
  header: string;
  subHeader: string;
  items: string[];
  cta?: TimelinePanelCta;
};

export type TimelineItem = {
  id: string;
  title: string;
  subtitle?: string;
  stage: TimelineStage;
  panel: TimelinePanelContent;
  isCalendarEvent?: boolean;
};

type TimelineNodeProps = Omit<TimelineItem, 'id' | 'panel'> & {
  size?: TimelineLayoutSize;
  isOpen: boolean;
  /** Id of the panel this node's toggle controls, for `aria-controls` and chevron toggle. */
  panelId: string;
  toggleOpenItem: () => void;
};

/**
 * Content rendered under or beside the node indicator depending on screen size as passed via size
 * prop from container. Mobile view positions the You're here badge beside the title within content.
 * Desktop positions this absolutely in container. The chevron is decorative - the whole
 * indicator + content row is the toggle, so this renders no interactive element of its own.
 */
const TimelineNodeContent = ({
  size = 'desktop',
  stage,
  title,
  subtitle,
  isOpen,
}: {
  size: TimelineLayoutSize;
  stage: TimelineStage;
  title: TimelineItem['title'];
  subtitle: TimelineItem['subtitle'];
  isOpen: boolean;
}) => {
  const chevronIconSize = size === 'desktop' ? '14' : '24';
  return (
    <div
      className={cx(
        size === 'mobile' ?
          'flex-1 flex items-start justify-between gap-4'
        : 'flex flex-col items-center gap-1',
      )}
    >
      <div
        className={cx(
          'flex flex-col gap-1',
          size === 'mobile' ? 'items-start text-left mt-1' : 'items-center text-center',
        )}
      >
        <div
          className={cx(
            'text-sm font-semibold  flex gap-1 items-center',
            isOpen ? 'text-brand-500' : 'text-gray-600',
          )}
        >
          {title}
          {size === 'mobile' && stage === 'in-progress' && <YoureHereBadge />}
        </div>
        {subtitle && (
          <p className={cx('text-xs', isOpen ? 'text-brand-500' : 'text-gray-400')}>{subtitle}</p>
        )}
      </div>
      <div className='text-xs font-semibold text-gray-400'>
        <Icon type={isOpen ? 'chevron-up' : 'chevron-down'} size={chevronIconSize} />
      </div>
    </div>
  );
};

const TimelineNodeIndicator = ({
  stage,
  size,
  isCalendarEvent = false,
  isOpen = false,
}: {
  stage: TimelineStage;
  size: TimelineLayoutSize;
  isCalendarEvent: TimelineItem['isCalendarEvent'];
  isOpen: boolean;
}) => {
  const iconSize = size === 'desktop' ? '16' : '12';
  return (
    <div
      className={cx(
        size === 'desktop' ? `h-[52px] w-[52px]` : `h-[32px] w-[32px]`,
        `rounded-full border flex items-center justify-center`,
        isOpen &&
          `border-brand-500 ${size === 'desktop' ? 'border-2' : 'border-[1.5px]'} bg-[#EEEEFD]`, // not exact to mock as mock calls for transparency in bg color which exposes connector
        stage === 'completed' && !isOpen && 'border-success-100 bg-white',
        stage === 'in-progress' && !isOpen && 'border-brand-500/30 bg-[#F7F7FF]',
        stage === 'not-started' && !isOpen && 'border-gray-100 bg-gray-50',
      )}
    >
      {/* Inner dot */}
      <div
        className={cx(
          size === 'desktop' ? 'h-10 w-10' : 'h-6 w-6',
          'rounded-full flex items-center justify-center',
          stage === 'completed' && 'bg-success-500 text-white',
          stage === 'in-progress' &&
            `bg-white ${size === 'desktop' ? 'border-2' : 'border-[1.5px]'} border-brand-500`,
          stage === 'not-started' && `bg-white border border-gray-200 text-gray-300`,
        )}
      >
        {stage === 'in-progress' && !isCalendarEvent && (
          <div
            className={cx(
              size === 'desktop' ? 'h-[11px] w-[11px]' : 'h-[7px] w-[7px]',
              'bg-brand-500 rounded-full',
            )}
          ></div>
        )}
        {isCalendarEvent && <Icon type='calendar-01' size={iconSize} />}
        {stage === 'completed' && !isCalendarEvent && <Icon type='check' size={iconSize} />}
      </div>
    </div>
  );
};

const DesktopTimelineNode = ({
  title,
  subtitle,
  stage,
  size = 'desktop',
  isCalendarEvent = false,
  isOpen,
  panelId,
  toggleOpenItem,
}: TimelineNodeProps) => {
  return (
    <div className='relative flex flex-col items-center gap-2'>
      {stage === 'in-progress' && (
        <div className='absolute -top-7.5'>
          <YoureHereBadge />
        </div>
      )}
      {/* The indicator and the content are one clickable area - the panel is rendered by the
          container below the timeline, so nothing interactive is nested inside this button. */}
      <AriaButton
        aria-label={`${isOpen ? 'Hide' : 'View'} ${title} details`}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onPress={toggleOpenItem}
        className={cx('flex w-full flex-col items-center gap-2')}
      >
        <TimelineNodeIndicator
          stage={stage}
          size={size}
          isCalendarEvent={isCalendarEvent}
          isOpen={isOpen}
        />
        <TimelineNodeContent
          title={title}
          subtitle={subtitle}
          isOpen={isOpen}
          size={size}
          stage={stage}
        />
      </AriaButton>
    </div>
  );
};

/**
 * Mobile lays the timeline out vertically: the node indicator sits in a left column with the
 * connector running beneath it, and the content (plus its open panel) fills the column beside it.
 * Unlike desktop, the connector is rendered per node rather than absolutely positioned over the
 * whole bar, since rows grow when a panel opens and so have no fixed/even height to divide up.
 */
const MobileTimelineNode = ({
  title,
  subtitle,
  stage,
  panel,
  size = 'mobile',
  isCalendarEvent = false,
  isLast = false,
  isOpen,
  panelId,
  toggleOpenItem,
}: TimelineNodeProps & {
  panel: TimelinePanelContent;
  /** The last node has no connector running below it */
  isLast?: boolean;
}) => {
  return (
    /* The bottom padding spaces the rows and is spanned by the connector */
    <div className={cx('relative flex w-full flex-col', !isLast && 'pb-6')}>
      {/* The connector runs from beneath the indicator to the bottom of the row, so it spans an open
          panel and the row spacing. It is positioned absolutely rather than stretched by a flex
          sibling so that the indicator and the content can sit inside a single button while the
          panel - which contains its own CTA - stays outside it. `left` centers the 2px line under
          the 32px indicator. */}
      {!isLast && (
        <div
          className={cx(
            'absolute left-[15px] top-8 bottom-0 w-[2px]',
            stage === 'completed' ? 'bg-success-500' : 'bg-gray-200',
          )}
        />
      )}
      {/* The indicator and the content are one clickable area */}
      <AriaButton
        aria-label={`${isOpen ? 'Hide' : 'View'} ${title} details`}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onPress={toggleOpenItem}
        className={cx('flex w-full items-start gap-3 text-left')}
      >
        <TimelineNodeIndicator
          stage={stage}
          size={size}
          isCalendarEvent={isCalendarEvent}
          isOpen={isOpen}
        />
        <TimelineNodeContent
          title={title}
          subtitle={subtitle}
          isOpen={isOpen}
          size={size}
          stage={stage}
        />
      </AriaButton>
      {/* Indented past the indicator (32px) and the row gap (12px) to align with the content */}
      {isOpen && (
        <div className='mt-3 pl-[44px]'>
          <CohortTimelinePanel id={panelId} {...panel} isCurrent={stage === 'in-progress'} />
        </div>
      )}
    </div>
  );
};

export const CohortTimelinePanel = ({
  id,
  header,
  subHeader,
  items,
  cta,
  isCurrent,
}: TimelinePanelContent & { id: string; isCurrent: boolean }) => {
  return (
    <div
      id={id}
      className={cx(
        'w-full  border border-gray-100 rounded-[10px] p-4',
        isCurrent ? 'bg-secondary-brand-25' : 'bg-gray-50',
      )}
    >
      <h4
        className={cx('text-sm font-bold mb-[9px]', isCurrent ? 'text-brand-500' : 'text-gray-700')}
      >
        {header}
      </h4>
      <h5 className='text-sm font-base text-gray-500 mb-[9px]'>{subHeader}</h5>
      <ul className='list-disc px-4'>
        {items.map((item) => (
          <li key={item} className='text-sm font-base text-gray-500'>
            {item}
          </li>
        ))}
      </ul>
      {cta && (
        <Button
          size='sm'
          variant='primary'
          aria-label={cta.label}
          onClick={cta.onClick}
          additionalClassnames='mt-[9px] w-full lg:w-fit'
        >
          {cta.label}
        </Button>
      )}
    </div>
  );
};

/**
 * Rough timeline UI component. This could be further abstracted if we consistently use this
 * layout - note that `@platform/components` already exports an unrelated `ProgressBar` and a `VerticalStepper`,
 * so a shared version of this would need to reconcile
 * with those. For now it supports just the cohort panel timeline.
 */
export const CohortTimeline = ({ items }: { items: TimelineItem[] }) => {
  const [openItemId, setOpenItemId] = useState<string>();
  // Possibly unnecessary for now - but does guard against dupe ids
  const idPrefix = useId();
  const openItem = items.find((item) => item.id === openItemId);
  const toggleOpenItem = (id: string) =>
    setOpenItemId((currentId) => (currentId === id ? undefined : id));
  /* The desktop and mobile layouts are both mounted and toggled with CSS, so their panels need
     distinct ids to keep each node's `aria-controls` pointing at a unique element. */
  const panelId = (id: string, size: TimelineLayoutSize) => `${idPrefix}-${size}-${id}`;

  return (
    /* `lg:pt-7` reserves the space taken up by the desktop "You're here" badge */
    <div className='relative lg:pt-7 lg:pb-1'>
      {/*
        Desktop view - Ugly
        The percentage-based `left` and `width` calculations, together with the
        `flex-1` layout of the node columns, position each connector evenly across
        the timeline. Each connector spans from the center of one node's column to
        the center of the next node's column. The `top` offset is the reserved badge
        space (32px) plus half a node indicator (26px) - padding does not shift an
        absolutely positioned child.
      */}
      <div className='absolute hidden lg:flex top-[58px] left-[26px] right-[26px]'>
        {items.slice(0, -1).map((item, i) => (
          <div
            key={item.id}
            className={cx(
              'absolute h-[2px]',
              item.stage === 'completed' ? 'bg-success-500' : 'bg-gray-200',
            )}
            style={{
              left: `${((i + 0.5) / items.length) * 100}%`,
              width: `${100 / items.length}%`,
            }}
          />
        ))}
      </div>
      {/* Nodes */}
      <div className='relative hidden lg:flex'>
        {items.map((item) => (
          <div key={item.id} className='flex-1 flex flex-col items-center'>
            <DesktopTimelineNode
              {...item}
              isOpen={item.id === openItemId}
              panelId={panelId(item.id, 'desktop')}
              toggleOpenItem={() => toggleOpenItem(item.id)}
            />
          </div>
        ))}
      </div>
      {/*
        Mobile view
        Each node owns the connector running below it so that rows are free to grow when their
        panel opens, rather than the connectors being positioned over evenly divided rows.
      */}
      <div className='flex flex-col lg:hidden'>
        {items.map((item, i) => (
          <MobileTimelineNode
            key={item.id}
            {...item}
            size='mobile'
            isLast={i === items.length - 1}
            isOpen={item.id === openItemId}
            panelId={panelId(item.id, 'mobile')}
            toggleOpenItem={() => toggleOpenItem(item.id)}
          />
        ))}
      </div>
      {/* On desktop display open node panel under timeline */}
      {openItem && (
        <div className='hidden lg:block mt-4'>
          <CohortTimelinePanel
            id={panelId(openItem.id, 'desktop')}
            {...openItem.panel}
            isCurrent={openItem.stage === 'in-progress'}
          />
        </div>
      )}
    </div>
  );
};

const YoureHereBadge = () => {
  return (
    <PillBadge color='success-25' size='sm' variant='plain'>
      You're here
    </PillBadge>
  );
};
